/**
 * 客户信息面板组件。
 *
 * 该组件用于在邮件撰写界面侧边展示与收件人匹配的客户信息，并提供邮件模板快捷插入功能。
 * 包含两个标签页：
 * - "客户检索"：根据撰写邮件的收件人地址，展示已匹配到的客户卡片和未匹配的邮箱地址（可一键创建客户）
 * - "邮件模板"：搜索并选择预设邮件模板，插入到邮件正文中（支持内联图片 cid 替换和客户变量替换）
 *
 * 设计要点：
 * - 标签页切换通过 `crm-tab-change` 自定义事件通知父组件
 * - 模板搜索使用 300ms 防抖，避免频繁请求
 * - 模板插入时自动替换 {客户名} 和 {公司名} 占位符为首个匹配客户的实际信息
 * - `composeSession` 属性变化时重置面板状态（切回客户检索标签页、清空模板搜索）
 *
 * 导出：`CrmCustomerInfoPanel`（自定义元素 `crm-customer-info-panel`）、`CustomerMatch` 接口、`TabDefinition` 接口
 *
 * 依赖模块：`emailTemplateApi`（模板 API）、`API_BASE_URL`（内联图片 URL 构建）、`BaseElement`
 */
import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../base-element.js";
import { emailTemplateApi, API_BASE_URL } from "../api.js";
import type { Customer, EmailTemplate } from "../types.js";

/** 客户匹配结果：一个客户对象 + 关联的邮箱地址列表 */
export interface CustomerMatch {
  customer: Customer;
  emails: string[];
}

/** 标签页定义：key 为标识符，label 为显示文本 */
export interface TabDefinition {
  key: string;
  label: string;
}

/**
 * 客户信息面板组件。
 *
 * 继承 `BaseElement`，通过 `@customElement("crm-customer-info-panel")` 注册为自定义元素。
 * 通常嵌入在邮件撰写界面的侧边栏中，数据由父组件通过 `matchedCustomers` 和 `unmatchedEmails` 属性传入。
 */
@customElement("crm-customer-info-panel")
export class CrmCustomerInfoPanel extends BaseElement {
  /** 已匹配到 CRM 客户的邮箱列表，每项含客户对象和关联邮箱 */
  @property({ type: Array }) matchedCustomers: CustomerMatch[] = [];
  /** 未匹配到 CRM 客户的邮箱地址列表，每项展示"创建客户"按钮 */
  @property({ type: Array }) unmatchedEmails: string[] = [];
  /** 标签页配置列表，默认包含"客户检索"和"邮件模板"两个标签 */
  @property({ type: Array }) tabs: TabDefinition[] = [
    { key: "info", label: "客户检索" },
    { key: "templates", label: "邮件模板" },
  ];
  /** 当前激活的标签页 key，默认为 "info" */
  @property({ type: String }) activeTab = "info";
  /** 数据加载中状态（由父组件控制，显示加载中文案） */
  @property({ type: Boolean }) loading = false;
  /** 邮件撰写会话编号，变化时触发面板状态重置（切换新邮件撰写时重置标签页和搜索） */
  @property({ type: Number }) composeSession = 0;

  /** 邮件模板列表 */
  @state() private templates: EmailTemplate[] = [];
  /** 模板搜索关键词 */
  @state() private templateSearch = "";
  /** 模板分页当前页码 */
  @state() private templatePage = 1;
  /** 模板分页总页数 */
  @state() private templateTotalPages = 1;
  /** 模板列表加载中状态 */
  @state() private templateLoading = false;

  /** 模板搜索防抖定时器 ID，null 表示无待执行搜索 */
  private _templateSearchTimer: number | null = null;
  /** 模板请求序号，仅允许最后一次搜索或分页请求更新面板。 */
  private _templateRequestId = 0;

  /**
   * 切换标签页。
   *
   * 切换激活标签页并向上派发 `crm-tab-change` 事件通知父组件。
   * 首次切换到"邮件模板"标签页时延迟加载模板列表。
   *
   * @param key - 目标标签页的 key
   */
  private _selectTab(key: string) {
    if (key === this.activeTab) return;
    this.activeTab = key;
    this.dispatchEvent(
      new CustomEvent("crm-tab-change", {
        detail: { key },
        bubbles: true,
        composed: true,
      }),
    );

    // 首次切换到模板标签页且模板未加载时，触发加载
    if (key === "templates" && this.templates.length === 0) {
      this._loadTemplates();
    }
  }

  /**
   * 触发创建客户事件。
   *
   * 向上派发 `crm-create-customer` 自定义事件，携带未匹配的邮箱地址。
   * 父组件监听后打开客户创建表单，预填该邮箱。
   *
   * @param email - 未匹配到客户的邮箱地址
   */
  private _createCustomer(email: string) {
    this.dispatchEvent(
      new CustomEvent("crm-create-customer", {
        detail: { email },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * 加载邮件模板列表。
   *
   * 调用 `emailTemplateApi.list` 分页查询启用状态（status=1）的模板，
   * 支持按关键词搜索。请求失败时清空模板列表并重置分页。
   */
  private async _loadTemplates() {
    const requestId = ++this._templateRequestId;
    this.templateLoading = true;
    try {
      const res = await emailTemplateApi.list({
        page: this.templatePage,
        per_page: 10,
        status: 1,
        keyword: this.templateSearch || undefined,
      });
      if (requestId !== this._templateRequestId) return;
      this.templates = res.data;
      this.templateTotalPages = res.pagination.pages;
    } catch {
      if (requestId === this._templateRequestId) {
        this.templates = [];
        this.templateTotalPages = 1;
      }
    } finally {
      if (requestId === this._templateRequestId) this.templateLoading = false;
    }
  }

  /**
   * 模板搜索输入事件处理器（带 300ms 防抖）。
   *
   * 每次输入更新搜索关键词，清除上一个防抖定时器，
   * 300ms 后若无新输入则重置到第 1 页并加载搜索结果。
   *
   * @param e - input 事件对象
   */
  private _onTemplateSearch(e: Event) {
    const input = e.target as HTMLInputElement;
    this.templateSearch = input.value;
    if (this._templateSearchTimer) clearTimeout(this._templateSearchTimer);
    this._templateSearchTimer = window.setTimeout(() => {
      this.templatePage = 1;
      this._loadTemplates();
    }, 300);
  }

  /**
   * 模板分页翻页（上一页/下一页）。
   *
   * 边界检查后更新页码并重新加载模板列表。
   *
   * @param delta - 页码增量，-1 为上一页，+1 为下一页
   */
  private _onTemplatePageChange(delta: number) {
    const newPage = this.templatePage + delta;
    if (newPage < 1 || newPage > this.templateTotalPages) return;
    this.templatePage = newPage;
    this._loadTemplates();
  }

  /**
   * 将 HTML 内容转为纯文本并截断为最多 80 个字符。
   *
   * 使用临时 div 元素解析 HTML，提取 textContent，超出 80 字符时追加省略号。
   * 用于模板列表中的预览展示。
   *
   * @param html - 原始 HTML 字符串
   * @returns 截断后的纯文本预览
   */
  private _stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.textContent || tmp.innerText || '';
    return text.length > 80 ? text.slice(0, 80) + '...' : text;
  }

  /**
   * 将选中的邮件模板插入到邮件正文中。
   *
   * 处理步骤：
   * 1. 替换内联图片的 cid: 引用为使用 Cookie 鉴权的实际文件 URL
   * 2. 替换 {客户名} 和 {公司名} 占位符为首个匹配客户的实际信息
   * 3. 向上派发 `insert-template` 事件，携带处理后的 HTML 内容
   *
   * @param template - 选中的邮件模板对象
   */
  private _insertTemplate(template: EmailTemplate) {
    let content = template.content;

    // 替换内联图片的 cid: 引用为使用 Cookie 鉴权的实际文件 URL
    for (const img of template.inline_images || []) {
      if (img.cid) {
        // 转义 cid 中的正则特殊字符，避免 RegExp 注入
        const escaped = img.cid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        content = content.replace(
          new RegExp(`cid:${escaped}`, 'g'),
          `${API_BASE_URL}/email-templates/inline-images/${img.id}/file`
        );
      }
    }

    // 替换客户变量占位符：优先使用客户名，回退到公司名（反之亦然）
    const firstCustomer = this.matchedCustomers[0]?.customer;
    if (firstCustomer) {
      content = content
        .replace(/\{客户名\}/g, firstCustomer.name || firstCustomer.company_name || '')
        .replace(/\{公司名\}/g, firstCustomer.company_name || firstCustomer.name || '');
    }

    this.dispatchEvent(
      new CustomEvent('insert-template', {
        detail: { content },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * 渲染单个匹配客户的卡片。
   *
   * 卡片包含：头像（客户名首字母大写）、客户名、关联邮箱、
   * 公司、联系人、职位、电话、地址、网站、备注、邮件数等信息。
   *
   * @param item - 客户匹配结果（含客户对象和邮箱列表）
   * @returns Lit 模板结果
   */
  private _renderCustomerCard(item: CustomerMatch) {
    const c = item.customer;
    const initial = (c.name || c.company_name || "?")[0].toUpperCase();

    return html`
      <div class="bg-surface border border-border rounded-lg p-3">
        <div class="flex items-center gap-2 mb-2 pb-2 border-b border-border-light">
          <div
            class="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0"
          >
            ${initial}
          </div>
          <div class="min-w-0">
            <div class="text-sm font-medium text-text truncate">
              ${c.name || c.company_name || "-"}
            </div>
            <div class="text-xs text-text-muted truncate">
              ${item.emails.join(", ")}
            </div>
          </div>
        </div>
        <div class="space-y-0.5 text-xs">
          <div class="flex justify-between">
            <span class="text-text-muted">公司</span
            ><span class="text-text truncate ml-2 text-right max-w-[60%]"
              >${c.company_name || "-"}</span
            >
          </div>
          <div class="flex justify-between">
            <span class="text-text-muted">联系人</span
            ><span class="text-text text-right">${c.name || "-"}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-text-muted">职位</span
            ><span class="text-text text-right">${c.job_title || "-"}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-text-muted">电话</span
            ><span class="text-text text-right">${c.phone || "-"}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-text-muted">地址</span
            ><span class="text-text text-right max-w-[60%] truncate">${c.address || "-"}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-text-muted">网站</span
            ><span class="text-text text-right max-w-[60%] truncate">${c.website || "-"}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-text-muted">备注</span
            ><span class="text-text text-right max-w-[60%] truncate">${c.remark || "-"}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-text-muted">邮件数</span
            ><span class="text-text text-right">${c.email_count || 0}</span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染"客户检索"标签页内容。
   *
   * 分为两个区域：
   * - 已匹配区域：展示匹配到的客户卡片列表
   * - 未匹配区域：展示未匹配邮箱列表，每项附"创建客户"按钮
   * 两者都为空时显示"添加收件人以检索客户信息"的引导文案。
   */
  private _renderInfoTab() {
    const hasMatched = this.matchedCustomers.length > 0;
    const hasUnmatched = this.unmatchedEmails.length > 0;
    const empty = !hasMatched && !hasUnmatched;

    if (empty) {
      return html`
        <div class="flex items-center justify-center h-full min-h-[200px]">
          <p class="text-sm text-text-muted text-center px-4">
                        添加收件人以检索客户信息
</p>
        </div>
      `;
    }

    return html`
      <div class="space-y-2">
        ${hasMatched
          ? html`
              <div>
                <div
                  class="text-xs font-medium text-text-muted mb-1.5"
                >
                  已匹配 (${this.matchedCustomers.length})
                </div>
                <div class="space-y-1.5">
                  ${this.matchedCustomers.map((item) =>
                    this._renderCustomerCard(item),
                  )}
                </div>
              </div>
            `
          : ""}
        ${hasUnmatched
          ? html`
              <div class="${hasMatched ? "pt-2 border-t border-border" : ""}">
                <div
                  class="text-xs font-medium text-text-muted mb-1.5"
                >
                  未匹配 (${this.unmatchedEmails.length})
                </div>
                ${this.unmatchedEmails.map(
                  (email) => html`
                    <div
                      class="flex items-center justify-between px-2 py-1.5 text-sm"
                    >
                      <span class="text-text-secondary truncate">${email}</span>
                      <button
                        class="text-primary hover:text-primary-hover text-xs font-medium cursor-pointer bg-transparent border-none p-0 shrink-0 ml-2"
                        @click=${() => this._createCustomer(email)}
                      >
                        + 创建客户
                      </button>
                    </div>
                  `,
                )}
              </div>
            `
          : ""}
      </div>
    `;
  }

  /**
   * 渲染"邮件模板"标签页内容。
   *
   * 包含搜索输入框、模板列表（每项含名称、内容预览和"插入"按钮）、
   * 以及分页控件（上一页/下一页，仅在总页数 > 1 时显示）。
   * 加载中显示"加载中..."，无结果时显示"暂无模板"或"输入关键词搜索"。
   */
  private _renderTemplatesTab() {
    return html`
      <div class="space-y-3">
        <input
          type="text"
          class="w-full px-3 py-1.5 text-sm border border-border rounded-md bg-surface"
          placeholder="搜索模板名称..."
          .value=${this.templateSearch}
          @input=${this._onTemplateSearch}
        />

        ${this.templateLoading ? html`
          <div class="flex items-center justify-center py-8">
            <p class="text-sm text-text-muted">加载中...</p>
          </div>
        ` : this.templates.length === 0 ? html`
          <div class="flex items-center justify-center py-8">
            <p class="text-sm text-text-muted">暂无模板</p>
          </div>
        ` : html`
          <div class="space-y-2">
            ${this.templates.map(t => html`
              <div class="bg-surface border border-border rounded-lg p-3">
                <div class="flex items-start justify-between gap-2">
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-text truncate">${t.name}</div>
                    <div class="text-xs text-text-muted mt-1 line-clamp-2">${this._stripHtml(t.content)}</div>
                  </div>
                  <button
                    class="text-primary hover:text-primary-hover text-xs font-medium cursor-pointer bg-transparent border-none p-0 shrink-0"
                    @click=${() => this._insertTemplate(t)}
                  >
                    插入
                  </button>
                </div>
              </div>
            `)}
          </div>

          ${this.templateTotalPages > 1 ? html`
            <div class="flex items-center justify-center gap-2 pt-2 border-t border-border">
              <button
                class="px-2 py-1 text-xs border border-border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                ?disabled=${this.templatePage === 1}
                @click=${() => this._onTemplatePageChange(-1)}
              >
                上一页
              </button>
              <span class="text-xs text-text-muted">${this.templatePage} / ${this.templateTotalPages}</span>
              <button
                class="px-2 py-1 text-xs border border-border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                ?disabled=${this.templatePage === this.templateTotalPages}
                @click=${() => this._onTemplatePageChange(1)}
              >
                下一页
              </button>
            </div>
          ` : ''}
        `}
      </div>
    `;
  }

  /**
   * 主渲染方法。
   *
   * 布局：顶部标签栏（可切换标签页）+ 底部内容区域。
   * 内容区域根据 activeTab 渲染对应标签页内容，loading 为 true 时显示加载中文案。
   */
  render() {
    return html`
      <div class="flex flex-col h-full">
        <div class="flex-shrink-0 flex border-b border-border px-2 pt-2">
          ${this.tabs.map(
            (tab) => html`
              <button
                class="px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-t-md -mb-px ${this
                  .activeTab === tab.key
                  ? "text-primary bg-surface border-t border-l border-r border-border"
                  : "text-text-muted hover:text-text bg-transparent border-none"}"
                @click=${() => this._selectTab(tab.key)}
              >
                ${tab.label}
              </button>
            `,
          )}
        </div>
        <div class="flex-1 overflow-y-auto p-3 border-l border-r border-b border-border rounded-b-md">
          ${this.loading
            ? html`
                <div class="flex items-center justify-center h-full min-h-[200px]">
                  <p class="text-sm text-text-muted">加载中...</p>
                </div>`
            : this.activeTab === "info"
              ? this._renderInfoTab()
              : this.activeTab === "templates"
                ? this._renderTemplatesTab()
                : ""}
        </div>
      </div>
    `;
  }

  /**
   * 生命周期：属性变化后触发。
   *
   * 当 `composeSession` 变化（用户切换到新的邮件撰写会话）时，
   * 重置面板状态：切回"客户检索"标签页、清空模板搜索、重置分页和模板列表、
   * 清除待执行的防抖定时器。
   *
   * @param changedProps - 变化的属性名到旧值的映射
   */
  updated(changedProps: Map<string, any>) {
    if (changedProps.has('composeSession') && this.composeSession > 0) {
      this.activeTab = "info";
      this.templateSearch = "";
      this.templatePage = 1;
      this.templates = [];
      this.templateTotalPages = 1;
      if (this._templateSearchTimer) clearTimeout(this._templateSearchTimer);
    }
  }

  /** 生命周期：组件卸载时清除防抖定时器，防止回调在组件销毁后执行 */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._templateSearchTimer) clearTimeout(this._templateSearchTimer);
  }
}
